import { useEffect, useState, useCallback, useMemo, memo } from "react";
import BlendFields from "./BlendFields";
import FuelForm from "./FuelForm";
import { FormControl, IconButton, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { AbsDropDown, Button } from "abs-ui-components";
import CloseIcon from '@mui/icons-material/Close';
import { Controller, FormProvider, useForm } from "react-hook-form";
import "./FuelRegistry.scss";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { useDispatch, useSelector } from "react-redux";
import { myFleetActions } from "../../../store/myFleet/myFleet.slice";
import { mapFuelToFormValues, META_MAP, DEFAULT_VALUES } from "./Helper";
import { createSelector } from '@reduxjs/toolkit';

const baseURL = process.env.REACT_APP_BASE_URL;
const NON_BLEND_TYPES = ["Fossil", "Biofuel", "e-Fuel"];

// selector: memoized fuel registry payload
const selectFuelRegistryPayload = createSelector(
    (state) => state.myFleet.fuelRegistryPayload,
    (payload) => {
        if (!payload) return [];
        return Array.isArray(payload) ? payload : JSON.parse(payload);
    }
);

const FuelFormcontainer = ({ onClose, fuelData, vesselMstId, onSuccess }) => {

    const dispatch = useDispatch();
    const isEditMode = !!fuelData;

    // parsed payload from redux
    const fuelRegistryPayloadParsed = useSelector(selectFuelRegistryPayload);

    // react-hook-form instance
    const methods = useForm({ defaultValues: DEFAULT_VALUES });
    const { reset, watch } = methods;

    const classificationType = watch("classificationType");

    // ui state
    const [fuelList, setFuelList] = useState([]);
    const [expandedMainPanel, setExpandedMainPanel] = useState("panel1");
    const [fuelForms, setFuelForms] = useState([1, 2]);

    // fetch fuel meta from API + reset form if editing
    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const response = await axiosHttp.get(`${baseURL}api/FuelRegistry/base`);
                setFuelList(response?.data?.data ?? []);
            } catch (err) {
                console.error("Error loading fuel metadata:", err);
            }
        };

        if (fuelData) reset(mapFuelToFormValues(fuelData));
        fetchMeta();
    }, []);

    // get metaType for metadata field based on base fuel list
    const getMetaTypeForFuel = useCallback((metaKey, fuelCode) => {
        const target = fuelList.find((x) => x.code === fuelCode);
        const match = target?.metadata?.find((m) => m.metaKey === metaKey);
        return match?.metaType ?? 1;
    }, [fuelList]);

    // build metadata array from form values
    const prepareMetaArrayForForm = useCallback((formValues = {}, fuelCode = null) => {
        const code = fuelCode || formValues.code;
        return Object.entries(META_MAP).map(([metaKey, formField]) => ({
            metaKey,
            metaValue: formValues[formField] ?? null,
            metaType: getMetaTypeForFuel(metaKey, code)
        }));
    }, [getMetaTypeForFuel]);

    // build final fuel payload
    const prepareFuelPayload = useCallback(
        (formValues, fuelClassification) => {
            const commonFields = {
                _tempId: formValues._tempId || crypto.randomUUID(),
                fuelRegId: formValues.fuelRegId || 0,
                parentFuelRegId: formValues.parentFuelRegId || 0,
                vesselMstId,
                categoryType: fuelClassification,
                code: formValues.code || "",
                name: formValues.name || "",
                mass: formValues.massMt ?? formValues.percentMass ?? "",
            };

            if (fuelClassification === "Blend") {
                // build child fuels
                const childFuels = (formValues.childFuels || []).map((child) => ({
                    _tempId: child._tempId || crypto.randomUUID(),
                    fuelRegId: child.fuelRegId || 0,
                    parentFuelRegId: child.parentFuelRegId || formValues.fuelRegId || 0,
                    vesselMstId,
                    categoryType: child.classificationType || fuelClassification,
                    code: child.code || "",
                    name: child.name || "",
                    mass: child.massMt ?? child.percentMass ?? "",
                    metadata: prepareMetaArrayForForm(child, child.code),
                    childFuels: []
                }));

                return [
                    {
                        ...commonFields,
                        metadata: prepareMetaArrayForForm(formValues),
                        childFuels
                    }
                ];
            }

            // non-blend payload
            return [
                {
                    ...commonFields,
                    metadata: prepareMetaArrayForForm(formValues),
                    childFuels: []
                }
            ];
        },
        [prepareMetaArrayForForm, vesselMstId]
    );

    const saveFuel = useCallback(
        async (fuelPayload) => {
            try {
                const res = await axiosHttp.post(`${baseURL}api/FuelRegistry/SetVesselFuel`, fuelPayload);
                return true;
                
            } catch (err) {
                console.error("Error checking BDN via API:", err);
                return true;
            }
        }
    );

    // check if BDN duplicate exists
    const isDuplicateBlendBDN = useCallback(
        async (payload, existingPayload, vesselMstId) => {
            for (const item of payload) {
                const bdnId = item.metadata.find((m) => m.metaKey === "BDN|ID")?.metaValue;
                if (!bdnId) continue;

                // server-side check
                if (vesselMstId > 0) {
                    try {
                        const res = await axiosHttp.get(`${baseURL}api/FuelRegistry/bdnidexists`, {
                            params: { bdnId, vesselMstId, fuelRegId: item.fuelRegId || 0 }
                        });

                        if (res?.data?.data === true) {
                            const setFuelRes = await axiosHttp.post(`${baseURL}api/FuelRegistry/SetVesselFuel`, item);
                            if (setFuelRes?.data?.data === true) return true;
                        }
                    } catch (err) {
                        console.error("Error checking BDN via API:", err);
                        return true;
                    }
                } else {
                    // local check
                    const existingBDNs = existingPayload
                        .filter(x => x._tempId !== item._tempId)
                        .map(x => x.metadata?.find(m => m.metaKey === "BDN|ID")?.metaValue)
                        .filter(Boolean);

                    if (existingBDNs.includes(bdnId)) return true;
                }
            }
            return false;
        },
        []
    );

    // merge new payload with existing local payload
    const getTheLatestPayload = useCallback((existingPayload, newPayload) => {
        if (!Array.isArray(existingPayload) || existingPayload.length === 0) return newPayload;

        const first = newPayload[0];
        const cleaned = existingPayload.filter((x) =>
            first.categoryType === "Blend"
                ? x._tempId !== first._tempId
                : !(x.code === first.code && x.categoryType === first.categoryType)
        );

        return [...cleaned, ...newPayload];
    }, []);

    // submit handler
    const onSubmit = async (data, e) => {
        if (e) e.preventDefault();

        try {
            const payload = prepareFuelPayload(data, data.classificationType);
            await saveFuel(payload[0]);
            
            // bdn duplicate validation
            /*if (await isDuplicateBlendBDN(payload, fuelRegistryPayloadParsed, vesselMstId)) {
                emitToasterMsg("error", "A Fuel with the same BDN ID already exists.");
                return;
            }

            let response = { data: { isSuccess: true } };
            dispatch(
                myFleetActions.saveFuelRegistryPayload({
                    fuelRegistryPayload: JSON.stringify(
                        getTheLatestPayload(fuelRegistryPayloadParsed, payload)
                    ),
                })
            );

            if (!response?.data?.isSuccess) {
                emitToasterMsg("error", response?.data?.errorMessage || "Error in saving fuel data");
            } else {
                emitToasterMsg("success", isEditMode ? "Fuel Updated Successfully" : "Fuel Added Successfully");
            }*/

            onClose?.();
            onSuccess?.();
        } catch (err) {
            console.error("Error submitting fuel data:", err);
        }
    };

    // cancel handler
    const handleCancel = () => {
        methods.reset();
        onClose?.();
    };

    // setup child fuel panels when editing blend
    useEffect(() => {
        if (fuelData?.FuelCategory === "Blend") {
            const count = (fuelData.subRows || fuelData.childFuels || []).length;
            if (count > 0) setFuelForms(Array.from({ length: count }, (_, i) => i + 1));
        }
    }, [fuelData]);

    // accordion handlers
    const handleMainChange = (panel) => (_, expanded) =>
        setExpandedMainPanel(expanded ? panel : false);

    const handleAddFuel = () =>
        setFuelForms(prev => [...prev, prev.length + 1]);

    const handleRemoveFuel = (index) => {

        const currentValues = methods.getValues();
        setFuelForms(prev => prev.filter((_, i) => i !== index));

        if (currentValues.childFuels && currentValues.childFuels[index]) {
            const updatedChildFuels = [...currentValues.childFuels];
            updatedChildFuels.splice(index, 1);
            methods.setValue('childFuels', updatedChildFuels);
        }

    };

    // memoized mapped selected fuel
    const selectedFuelMapped = useMemo(
        () => (fuelData ? mapFuelToFormValues(fuelData) : {}),
        [fuelData]
    );
    const disableChildDelete = isEditMode && !!selectedFuelMapped?.vesselMstId;

    return (
        <FormProvider {...methods}>
            <div className="voyage-modal">
                <form
                    className="fuelregistry-form-container"
                    onSubmit={(e) => {
                        e.preventDefault();
                        methods.handleSubmit(onSubmit)(e);
                    }}
                >
                    <div style={{ padding: "1rem" }}>

                        {/* -- Fuel classification selector -- */}
                        <FormControl fullWidth>
                            <div>Fuel Classification</div>

                            <Controller
                                name="classificationType"
                                control={methods.control}
                                render={({ field }) => (
                                    <div style={{ width: "200px" }}>
                                        <AbsDropDown
                                            options={[
                                                { label: "Fossil", value: "Fossil" },
                                                { label: "Blend", value: "Blend" },
                                                { label: "Biofuel", value: "Biofuel" },
                                                { label: "e-Fuel", value: "e-Fuel" }
                                            ]}
                                            selected={field.value}
                                            handleChange={(o) => field.onChange(o?.value || "")}
                                            isClearable
                                            disabled={isEditMode}
                                            ref={field.ref}
                                        />
                                    </div>
                                )}
                            />
                        </FormControl>

                        {/* -- Blend mode -- */}
                        {classificationType === "Blend" && (
                            <>
                                <BlendFields
                                    fuelClassification={classificationType}
                                    fuel={selectedFuelMapped}
                                    isEditMode={isEditMode}
                                />

                                {fuelForms.map((num, index) => (
                                    <Accordion
                                        key={index}
                                        expanded={expandedMainPanel === `panel${num}`}
                                        onChange={handleMainChange(`panel${num}`)}
                                        style={{ marginBottom: "5px" }}
                                    >
                                        <AccordionSummary className="accordion-header">
                                            <Typography>{`Fuel Type ${num}`}</Typography>

                                            {index >= 2 && !(isEditMode && vesselMstId > 0) && (
                                                <IconButton
                                                    style={{ marginLeft: "590px", marginTop: "-10px" }}
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveFuel(index);
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" style={{ color: "white" }} />
                                                </IconButton>
                                            )}

                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <FuelForm
                                                fuelClassification={classificationType}
                                                selectedFuel={selectedFuelMapped}
                                                baseFuelList={fuelList}
                                                isBlendComponent
                                                formIndex={index}
                                                isEditMode={isEditMode}
                                                numberOfForms={fuelForms.length}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                ))}

                                {!(isEditMode && vesselMstId > 0) && (
                                    <Button onClick={handleAddFuel} style={{ marginTop: "10px" }}>
                                        Add Another Fuel
                                    </Button>
                                )}
                            </>
                        )}

                        {/* -- Non-blend types -- */}
                        {NON_BLEND_TYPES.includes(classificationType) && (
                            fuelList.length > 0 && (
                                <FuelForm
                                    fuelClassification={classificationType}
                                    selectedFuel={selectedFuelMapped}
                                    baseFuelList={fuelList}
                                    isEditMode={isEditMode}
                                />
                            )
                        )}

                        {/* -- No classification selected -- */}
                        {!classificationType && (
                            <Typography variant="body2" color="text.secondary">
                                Please select a fuel classification.
                            </Typography>
                        )}
                    </div>

                    {/* -- Action buttons -- */}
                    <div className="d-flex justify-content-end mr-3 center-button">
                        <button
                            type="submit"
                            className="save-button mr-2"
                            onClick={methods.handleSubmit(onSubmit)}
                        >
                            {isEditMode ? "Update" : "Add"}
                        </button>

                        <button
                            type="button"
                            className="clear-button mr-3"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </FormProvider>
    );
};

export default memo(FuelFormcontainer);